import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// Register html2canvas globally so jsPDF's .html() method can find it.
// jsPDF checks window.html2canvas before trying a dynamic import() which
// fails in this project's single-file esbuild bundle (no code-splitting).
;(window as any).html2canvas = html2canvas

/**
 * Renders an HTML string directly to a PDF and triggers a browser download.
 * Uses jsPDF .html() backed by html2canvas.
 */
export async function downloadHtmlAsPdf(
  html: string,
  filename: string,
): Promise<void> {
  // Parse <style> and <body> content out of a full HTML document string.
  let content = html
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
  if (bodyMatch) {
    const styleMatch = html.match(/<style[^>]*>[\s\S]*?<\/style>/gi)
    content = (styleMatch ? styleMatch.join('') : '') + bodyMatch[1]
  }

  // A4 = 595.28pt wide. 30pt margins → 535pt content area.
  // Container is 680px, scaled to 535pt → factor ≈ 0.787.
  // This gives a good balance: enough room for pills/tables,
  // and mild enough scaling that text stays sharp.
  const CONTAINER_W = 680
  const MARGIN = 30 // pt
  const CONTENT_W = 595.28 - MARGIN * 2 // ≈ 535pt
  const SCALE = CONTENT_W / CONTAINER_W   // ≈ 0.787

  // Redirect "body {" rules to the container so padding/font styles apply to
  // the PDF content div instead of the real page <body>.
  const scoped = content.replace(/\bbody\s*\{/g, '#__pdf_root__ {')

  const container = document.createElement('div')
  container.id = '__pdf_root__'
  container.style.cssText =
    `position:fixed;left:0;top:0;width:${CONTAINER_W}px;z-index:99999;background:#fff;overflow:hidden;`
  container.innerHTML = scoped
  document.body.appendChild(container)

  // Wait two animation frames so the browser fully paints the content.
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  )

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })

  try {
    await doc.html(container, {
      x: MARGIN,
      y: MARGIN,
      width: CONTENT_W,
      windowWidth: CONTAINER_W,
      autoPaging: 'text',
      margin: [MARGIN, MARGIN, MARGIN, MARGIN],
      html2canvas: {
        scale: SCALE,
        useCORS: true,
        logging: false,
      },
    })
    doc.save(filename)
  } finally {
    document.body.removeChild(container)
  }
}
